import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { trustApi, matterApi, type TrustTransaction, type MatterSummary } from '../api';
import Select from '../components/common/Select';
import { humanise } from '../lib/labels';

// Section 86 trust ledger. Balances are computed by the API based on `kind` —
// the column on the wire is `amount_zar` (always positive); deposits add,
// every other kind subtracts (except `adjustment` which carries its own sign).

const KIND_OPTIONS = [
  { value: 'deposit',           label: 'Deposit (in)' },
  { value: 'fees_drawn',        label: 'Fees drawn (out)' },
  { value: 'disbursement_paid', label: 'Disbursement paid (out)' },
  { value: 'refund_to_client',  label: 'Refund to client (out)' },
  { value: 'transfer_out',      label: 'Transfer out' },
  { value: 'adjustment',        label: 'Adjustment' },
] as const;

type Kind = typeof KIND_OPTIONS[number]['value'];

const ZAR = (n: number | null | undefined) =>
  n == null ? '—' : new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(n);

const SIGN: Record<Kind, 1 | -1> = {
  deposit: 1,
  fees_drawn: -1,
  disbursement_paid: -1,
  refund_to_client: -1,
  transfer_out: -1,
  adjustment: 1,
};

export default function Trust() {
  const [matters, setMatters] = useState<MatterSummary[]>([]);
  const [matterId, setMatterId] = useState<string>('');
  const [txns, setTxns] = useState<TrustTransaction[]>([]);
  const [balance, setBalance] = useState<number | null>(null);
  const [allBalances, setAllBalances] = useState<{ matter_id: number; balance_zar: number }[]>([]);
  const [totalZar, setTotalZar] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);

  const matterById = useMemo(() => {
    const m = new Map<string, MatterSummary>();
    matters.forEach((x) => m.set(String(x.ROWID), x));
    return m;
  }, [matters]);

  // Load matters once for the dropdown + the balances sweep.
  useEffect(() => {
    matterApi.list({}).then((r) => setMatters(r.matters || [])).catch(() => null);
  }, []);

  // Refresh whenever the selected matter changes (or on mount with '' = all).
  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [list, sweep] = await Promise.all([
        trustApi.list(matterId ? Number(matterId) : undefined),
        trustApi.balances().catch(() => ({ balances: [], total_zar: 0 })),
      ]);
      setTxns(list.transactions);
      setBalance(list.balance_zar);
      setAllBalances(sweep.balances);
      setTotalZar(sweep.total_zar);
    } catch (e) {
      setError((e as Error).message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [matterId]);

  return (
    <div className="stack">
      <header>
        <h1>Trust ledger</h1>
        <p className="muted">
          Section 86 trust account transactions. Withdrawals that would overdraw a matter's
          trust balance are refused at the API level. Bank reconciliation is via CSV import only.
        </p>
      </header>

      {/* --- Tenant-wide balance summary --- */}
      <div className="row" style={{ gap: 12 }}>
        <div className="card grow">
          <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total trust held</div>
          <div style={{ fontSize: 26, fontWeight: 700, marginTop: 6 }}>{ZAR(totalZar)}</div>
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>across {allBalances.length} matter(s)</div>
        </div>
        {matterId && balance !== null && (
          <div className="card grow">
            <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Selected matter</div>
            <div style={{ fontSize: 26, fontWeight: 700, marginTop: 6 }}>{ZAR(balance)}</div>
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{matterById.get(matterId)?.matter_name || `#${matterId}`}</div>
          </div>
        )}
      </div>

      {/* --- Filter + record action --- */}
      <div className="row">
        <div className="grow">
          <label style={{ fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Matter</label>
          <Select
            value={matterId}
            onChange={(v) => setMatterId(v)}
            searchPlaceholder="Filter by matter…"
            options={[
              { value: '', label: 'All matters with a trust balance' },
              ...allBalances.map((b) => {
                const m = matterById.get(String(b.matter_id));
                return {
                  value: String(b.matter_id),
                  label: `${m?.reference || `#${b.matter_id}`} — ${m?.matter_name || ''} · ${ZAR(b.balance_zar)}`,
                };
              }),
            ]}
          />
        </div>
        <button
          className="primary"
          onClick={() => setRecording(true)}
          disabled={!matterId}
          title={matterId ? 'Record a trust transaction on this matter' : 'Pick a matter first'}
        >
          + Record transaction
        </button>
      </div>

      {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}

      {/* --- Transactions table --- */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div className="muted" style={{ padding: 18 }}>Loading…</div>
        ) : txns.length === 0 ? (
          <div className="muted" style={{ padding: 18 }}>
            {matterId
              ? 'No trust transactions on this matter yet.'
              : 'No trust transactions across any matter yet. Record a deposit on a matter to get started.'}
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Date</th>
                {!matterId && <th>Matter</th>}
                <th>Kind</th>
                <th>Counterparty / reference</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {txns.map((t) => {
                const sign = SIGN[t.kind] || 1;
                const m = matterById.get(String(t.matter_id));
                return (
                  <tr key={String(t.ROWID)}>
                    <td>{t.txn_date}</td>
                    {!matterId && (
                      <td>
                        <Link to={`/matters/${t.matter_id}`}>
                          {m?.reference || `#${t.matter_id}`}
                        </Link>
                      </td>
                    )}
                    <td><span className="pill">{humanise(t.kind)}</span></td>
                    <td className="muted" style={{ fontSize: 12 }}>
                      {t.counterparty || '—'}
                      {t.reference && <span style={{ marginLeft: 8 }}>· ref {t.reference}</span>}
                    </td>
                    <td style={{
                      textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                      color: sign > 0 ? 'var(--color-success)' : 'var(--color-text)',
                      fontWeight: 600,
                    }}>
                      {sign > 0 ? '+ ' : '− '}{ZAR(t.amount_zar)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {recording && matterId && (
        <RecordModal
          matterId={matterId}
          matterLabel={matterById.get(matterId)?.matter_name || `#${matterId}`}
          currentBalance={balance ?? 0}
          onClose={() => setRecording(false)}
          onSaved={() => { setRecording(false); load(); }}
        />
      )}
    </div>
  );
}

// ---------- Record modal ----------

function RecordModal({
  matterId, matterLabel, currentBalance, onClose, onSaved,
}: {
  matterId: string;
  matterLabel: string;
  currentBalance: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    txn_date: today,
    kind: 'deposit' as Kind,
    amount: '',
    counterparty: '',
    reference: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const amt = Number(form.amount);
  const sign = SIGN[form.kind];
  const projected = Number.isFinite(amt) && amt > 0 ? currentBalance + sign * amt : currentBalance;

  const save = async () => {
    if (!Number.isFinite(amt) || amt <= 0) { setError('Amount must be a positive number.'); return; }
    setSaving(true); setError(null);
    try {
      await trustApi.record({
        matter_id: Number(matterId),
        txn_date: form.txn_date,
        kind: form.kind,
        amount_zar: amt,
        counterparty: form.counterparty || undefined,
        reference: form.reference || undefined,
        notes: form.notes || undefined,
      });
      onSaved();
    } catch (e) {
      // Server returns insufficient_trust_funds if the withdrawal would overdraw.
      setError((e as Error).message);
    } finally { setSaving(false); }
  };

  return (
    <div style={backdrop} onClick={onClose}>
      <div className="card" style={modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginBottom: 4 }}>Record trust transaction</h3>
        <p className="muted" style={{ fontSize: 13, marginBottom: 14 }}>
          Matter: <b>{matterLabel}</b> · current balance: <b>{ZAR(currentBalance)}</b>
        </p>

        <div className="stack">
          <div className="row">
            <div className="grow">
              <label>Kind</label>
              <Select
                value={form.kind}
                onChange={(v) => setForm({ ...form, kind: v as Kind })}
                options={[...KIND_OPTIONS]}
              />
            </div>
            <div style={{ width: 200 }}>
              <label>Date</label>
              <input type="date" value={form.txn_date} onChange={(e) => setForm({ ...form, txn_date: e.target.value })} />
            </div>
          </div>

          <div>
            <label>Amount (ZAR — always positive)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              autoFocus
            />
            {form.amount && (
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                After this transaction: <b style={{ color: projected < 0 ? 'var(--color-danger)' : 'var(--color-text)' }}>
                  {ZAR(projected)}
                </b>
                {projected < 0 && ' — server will refuse this (overdraft).'}
              </div>
            )}
          </div>

          <div className="row">
            <div className="grow">
              <label>Counterparty (payer / payee)</label>
              <input
                value={form.counterparty}
                onChange={(e) => setForm({ ...form, counterparty: e.target.value })}
                placeholder="e.g. ABSA EFT from client"
              />
            </div>
            <div className="grow">
              <label>Bank reference (for matching CSV)</label>
              <input
                value={form.reference}
                onChange={(e) => setForm({ ...form, reference: e.target.value })}
                placeholder="e.g. EFT123456"
              />
            </div>
          </div>

          <div>
            <label>Notes (optional)</label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>

          {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}

          <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={onClose} disabled={saving}>Cancel</button>
            <button className="primary" onClick={save} disabled={saving || projected < 0}>
              {saving ? 'Saving…' : 'Record'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
};
const modal: React.CSSProperties = { width: 620, maxWidth: '92vw' };
